import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, StatCard, Pill } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Heart, ShareNetwork, WhatsappLogo, Copy, ChartLineUp, Users,
  Sparkle, ArrowRight, GiftIcon,
} from "@phosphor-icons/react";

const SHARE_TEMPLATE = (link, name) =>
  `Hi! ${name ? name + " here." : ""} Our family loves MangoCubs Preschool — daily photo updates, gentle teachers, and a really thoughtful app. If you're looking for a preschool, please use my invite link — both our families get a fee credit when your child joins. ${link}`;

export default function ParentReferAFriend() {
  const { user } = useAuth();
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setRef(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !ref) {
    return <div className="text-center py-16 text-ink-soft">Loading…</div>;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const inviteLink = `${origin}/enquiry?ref=${ref.code}`;
  const waMessage = SHARE_TEMPLATE(inviteLink, user?.name);
  const waUrl = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Invite link copied!");
    } catch { toast.error("Could not copy"); }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join us at MangoCubs",
          text: SHARE_TEMPLATE(inviteLink, user?.name),
          url: inviteLink,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Refer a friend"
        title="Share the love. Earn fee credits."
        subtitle="When a friend joins through your invite, you both get ₹1,500 in fee credit. Plus, your child gets a new buddy 🌸"
      />

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2 relative rounded-3xl overflow-hidden bg-gradient-to-br from-terracotta to-terracotta-hover text-white p-7"
          data-testid="referral-hero"
        >
          <div className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-mustard/30 blur-3xl" />
          <div className="absolute top-6 right-6 text-6xl opacity-70 animate-float">🎁</div>
          <div className="text-xs uppercase tracking-[0.18em] opacity-80">Your invite code</div>
          <div className="font-heading text-5xl font-bold tracking-[0.08em] mt-1" data-testid="referral-code">{ref.code}</div>
          <div className="mt-4 text-white/85 text-sm break-all">
            <span className="opacity-80">Link:</span> {inviteLink}
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white font-semibold hover:bg-[#1DA851] transition"
              data-testid="referral-share-whatsapp"
            >
              <WhatsappLogo weight="fill" /> Share on WhatsApp
            </a>
            <button
              onClick={shareNative}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-terracotta font-semibold hover:bg-mustard-light transition"
              data-testid="referral-share-native"
            >
              <ShareNetwork weight="duotone" /> Share
            </button>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 text-white font-semibold border border-white/20 hover:bg-white/25 transition"
              data-testid="referral-copy"
            >
              <Copy weight="duotone" /> Copy link
            </button>
          </div>
        </motion.div>

        <Panel testid="referral-stats">
          <div className="text-sm text-ink-soft font-medium mb-3">Your impact</div>
          <div className="space-y-4">
            <Stat icon={Users} label="Friends who enquired" value={ref.enquiry_count} accent="lavender" />
            <Stat icon={ChartLineUp} label="Friends who joined" value={ref.conversion_count} accent="sage" />
            <Stat icon={GiftIcon} label="Fee credit earned" value={`₹${ref.credit_earned.toLocaleString("en-IN")}`} accent="mustard" />
          </div>
        </Panel>
      </div>

      <Panel title="How it works">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { n: 1, t: "Share your link", b: "WhatsApp it, copy it, or use Share. Friends use it to enquire." },
            { n: 2, t: "Friend joins", b: "When their admission is signed and confirmed, you both unlock credit." },
            { n: 3, t: "Apply credit", b: "₹1,500 credit auto-applies to your next quarterly invoice. They get ₹1,500 too." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl bg-paper-alt p-5">
              <div className="w-9 h-9 rounded-full bg-terracotta text-white font-bold flex items-center justify-center">{s.n}</div>
              <div className="font-heading font-semibold mt-3">{s.t}</div>
              <div className="text-sm text-ink-soft mt-1">{s.b}</div>
            </div>
          ))}
        </div>
      </Panel>

      {ref.events?.length > 0 && (
        <Panel title="Recent activity" className="mt-6">
          <div className="space-y-2">
            {ref.events.slice().reverse().slice(0, 10).map((e, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-2xl bg-paper-alt">
                <div className="flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${e.kind === "conversion" ? "bg-sage-soft text-sage-deep" : "bg-mustard-soft text-mustard-hover"}`}>
                    {e.kind === "conversion" ? <Sparkle weight="duotone" /> : <Heart weight="duotone" />}
                  </span>
                  <div>
                    <div className="font-semibold text-sm capitalize">{e.kind === "enquiry" ? "New enquiry" : "🎉 Joined MangoCubs"}</div>
                    {e.child_name && <div className="text-xs text-ink-soft">{e.child_name}</div>}
                  </div>
                </div>
                <div className="text-xs text-ink-soft">{new Date(e.ts).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  const map = {
    sage: "bg-sage-soft text-sage-deep",
    mustard: "bg-mustard-soft text-mustard-hover",
    lavender: "bg-lavender-soft text-lavender-deep",
    terracotta: "bg-terracotta-soft text-terracotta",
  };
  return (
    <div className="flex items-center gap-3">
      <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${map[accent]}`}>
        <Icon size={20} weight="duotone" />
      </span>
      <div>
        <div className="text-xs text-ink-soft uppercase tracking-wider">{label}</div>
        <div className="font-heading font-bold text-2xl mt-0.5">{value}</div>
      </div>
    </div>
  );
}
